import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { IconButton } from '@fluentui/react/lib/Button';
import { Dropdown } from '@fluentui/react/lib/Dropdown';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';

const getStyles = () => {
  const pagerIcon = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    background: '#FFFFFF',
    padding: '27px 0',
  };

  const pageNavigationButton = {
    background: 'transparent',
  };

  const pageNumber = {
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '12px',
    lineHeight: '32px',
    color: '#323130',
    alignItems: 'center',
    display: 'flex',
  };

  const totalPageNumber = {
    marginLeft: 8,
  };

  return {
    pagerIcon,
    pageNavigationButton,
    pageNumber,
    totalPageNumber,
  };
};

const PageNavigationFooterUnstyled = (props) => {
  const {
    pageIndex,
    totalPageNumber,
    onNextPageClick,
    onPreviousPageClick,
    onRandomPageSelect,
    classes,
    i18n,
  } = props;

  const nextPageHandler = useCallback(() => {
    if (onNextPageClick) {
      onNextPageClick();
    }
  }, [onNextPageClick]);

  const previousPageHandler = useCallback(() => {
    if (onPreviousPageClick) {
      onPreviousPageClick();
    }
  }, [onPreviousPageClick]);

  const randomPagingHandler = useCallback((evt, item) => {
    if (onRandomPageSelect) {
      onRandomPageSelect(item.key - 1);
    }
  }, [onRandomPageSelect]);

  const firstPageHandler = useCallback(() => {
    if (onRandomPageSelect) {
      onRandomPageSelect(0);
    }
  }, [onRandomPageSelect]);

  const lastPageHandler = useCallback(() => {
    if (onRandomPageSelect) {
      onRandomPageSelect(totalPageNumber - 1);
    }
  }, [onRandomPageSelect, totalPageNumber]);

  const pageIndexContent = useMemo(() => (
    curlyTemplate('of {{totalPageNumber}}')({ totalPageNumber })
  ), [totalPageNumber]);

  const dropdownOptions = useMemo(() => {
    const options = [];

    for (let index = 1; index <= totalPageNumber; index += 1) {
      options.push({ key: index, text: index });
    }

    return options;
  }, [totalPageNumber]);

  return (
    <nav
      className={classNames(classes.pagerIcon, 'pager-navigation')}
      aria-label={i18n.getString(_TL_('Pagination'))}
    >
      <IconButton
        className={classNames(classes.pageNavigationButton, 'first-page')}
        iconProps={{ iconName: 'DoubleChevronLeft' }}
        onClick={firstPageHandler}
        disabled={pageIndex === 0}
        ariaLabel={i18n.getString(_TL_('Go to first page'))}
      />
      <IconButton
        className={classNames(classes.pageNavigationButton, 'previous-page')}
        iconProps={{ iconName: 'ChevronLeft' }}
        onClick={previousPageHandler}
        disabled={pageIndex === 0}
        ariaLabel={i18n.getString(_TL_('Previous page'))}
      />
      <span className={classNames(classes.pageNumber, 'page-number')}>
        <Dropdown
          className={classes.dropdown}
          selectedKey={pageIndex + 1}
          onChange={randomPagingHandler}
          options={dropdownOptions}
          styles={{ dropdown: { width: 70 } }}
          ariaLabel={i18n.getString(_TL_('Current page'))}
          disabled={totalPageNumber === 0}
        />
        <span className={classes.totalPageNumber}>{pageIndexContent}</span>
      </span>
      <IconButton
        className={classNames(classes.pageNavigationButton, 'next-page')}
        iconProps={{ iconName: 'ChevronRight' }}
        onClick={nextPageHandler}
        disabled={pageIndex === totalPageNumber - 1 || totalPageNumber === 0}
        ariaLabel={i18n.getString(_TL_('Next page'))}
      />
      <IconButton
        className={classNames(classes.pageNavigationButton, 'last-page')}
        iconProps={{ iconName: 'DoubleChevronRight' }}
        onClick={lastPageHandler}
        disabled={pageIndex === totalPageNumber - 1 || totalPageNumber === 0}
        ariaLabel={i18n.getString(_TL_('Go to last page'))}
      />
    </nav>
  );
};

PageNavigationFooterUnstyled.propTypes = {
  pageIndex: PropTypes.number.isRequired,
  totalPageNumber: PropTypes.number.isRequired,
  onNextPageClick: PropTypes.func.isRequired,
  onPreviousPageClick: PropTypes.func.isRequired,
  onRandomPageSelect: PropTypes.func.isRequired,
  classes: PropTypes.shape(_.mapObject(
    getStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
};

export const PageNavigationFooter = withDefaultStyles(PageNavigationFooterUnstyled, getStyles);
